<!-- Register route: path: '/org/:sub_level/:id/wisdom-care-volunteers/:volunteerId', component: WisdomCareVolunteerDetail -->
<template>
    <div class="section text-center mt-4" v-if="loading">
        <div class="spinner-border text-primary" role="status"></div>
    </div>

    <template v-else-if="volunteer">
        <!-- Profile -->
        <div class="section pt-2">
            <div class="wc-card">
                <div class="wc-card-header">
                    <span class="wc-card-title">Profile</span>
                    <span v-if="volunteer.verified" class="wc-verified-badge verified">✓ Verified</span>
                    <span v-else class="wc-verified-badge not-verified">Not Verified</span>
                </div>
                <div class="wc-card-body">
                    <div class="wc-row"><span class="wc-label">Name</span><span class="wc-val">{{ volunteer.name }}</span></div>
                    <div class="wc-row"><span class="wc-label">Mobile</span><span class="wc-val">{{ volunteer.mobile }}</span></div>
                    <div class="wc-row"><span class="wc-label">Gender</span><span class="wc-val">{{ volunteer.gender || '-' }}</span></div>
                    <div class="wc-row"><span class="wc-label">DOB</span><span class="wc-val">{{ formatDate(volunteer.dob) }}</span></div>
                    <div class="wc-row"><span class="wc-label">Email</span><span class="wc-val">{{ volunteer.email || '-' }}</span></div>
                    <div class="wc-row"><span class="wc-label">Address</span><span class="wc-val">{{ volunteer.address || '-' }}</span></div>
                    <div class="wc-row"><span class="wc-label mal-text">District</span><span class="wc-val mal-text">{{ volunteer.district?.name || '-' }}</span></div>
                    <div class="wc-row"><span class="wc-label mal-text">Zone</span><span class="wc-val mal-text">{{ volunteer.zone?.name || '-' }}</span></div>
                    <div class="wc-row"><span class="wc-label mal-text">Unit</span><span class="wc-val mal-text">{{ volunteer.unit?.name || '-' }}</span></div>
                    <div class="wc-service-categories" v-if="volunteer.service_categories && volunteer.service_categories.length">
                        <span class="wc-chip" v-for="c in volunteer.service_categories" :key="c.id">{{ c.name }}</span>
                    </div>
                    <!-- Freeform skills the volunteer typed themselves (not from the catalog —
                         see WisdomCare.vue's "വേറെ മേഖല ടൈപ്പ് ചെയ്യുക" field), kept visually
                         distinct from the catalog chips above since they're user-added, not
                         admin-managed. -->
                    <div class="wc-service-categories" v-if="volunteer.custom_skills && volunteer.custom_skills.length" style="margin-top:6px;">
                        <span class="wc-chip wc-chip-custom" v-for="(s, idx) in volunteer.custom_skills" :key="'cs' + idx">{{ s }}</span>
                    </div>
                    <button type="button" class="btn btn-block mt-2" :class="volunteer.verified ? 'btn-outline-danger' : 'btn-success'" :disabled="verifySaving" @click="toggleVerify">
                        <span v-if="verifySaving" class="spinner-border spinner-border-sm me-1"></span>
                        {{ volunteer.verified ? 'Unverify' : 'Verify' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Verified Skill Badges — admin/coordinator awards a category badge based on the
             volunteer's confirmed skills/training/experience. Separate from the self-selected
             Service Categories above: a badge means an admin actually verified it. -->
        <div class="section pt-0 mt-2">
            <div class="wc-card">
                <div class="wc-card-header">
                    <span class="wc-card-title">Verified Skill Badges</span>
                    <button type="button" class="btn btn-sm btn-primary" @click="openBadgeModal">
                        <ion-icon name="ribbon-outline"></ion-icon> Award Badge
                    </button>
                </div>
                <div class="wc-card-body">
                    <div v-if="!badges.length" class="text-center text-muted py-2">No badges awarded yet.</div>
                    <div v-for="vb in badges" :key="vb.id" class="wc-list-item">
                        <div class="wc-badge-medal-sm" :style="badgeMedalStyle(vb.badge)">
                            <span v-if="vb.badge && vb.badge.icon">{{ vb.badge.icon }}</span>
                            <ion-icon v-else name="ribbon"></ion-icon>
                        </div>
                        <div class="wc-list-item-main">
                            <b>{{ vb.badge?.name || '-' }}</b>
                            <div class="text-muted mal-text" style="font-size:12px;margin-top:2px;" v-if="vb.badge && vb.badge.name_mal">{{ vb.badge.name_mal }}</div>
                            <div class="text-muted" style="font-size:12px;" v-if="vb.note">{{ vb.note }}</div>
                            <div class="text-muted" style="font-size:11px;">Awarded by {{ vb.awarded_by_name || '-' }} &middot; {{ formatDate(vb.awarded_at) }}</div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-danger" @click="revokeBadge(vb)">Revoke</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Organizational Trainings -->
        <div class="section pt-0 mt-2">
            <div class="wc-card">
                <div class="wc-card-header">
                    <span class="wc-card-title">Organizational Trainings</span>
                    <button type="button" class="btn btn-sm btn-primary" @click="openTrainingModal">
                        <ion-icon name="add-outline"></ion-icon> Add
                    </button>
                </div>
                <div class="wc-card-body">
                    <p class="text-muted" style="font-size:12px;margin:0 0 8px;">Office-recorded attendance — the volunteer cannot add these themselves.</p>
                    <div v-if="!orgTrainings.length" class="text-center text-muted py-2">No organizational trainings recorded.</div>
                    <div v-for="t in orgTrainings" :key="t.id" class="wc-list-item">
                        <div class="wc-list-item-main">
                            <b>{{ t.title }}</b>
                            <div class="text-muted" style="font-size:12px;">
                                <span v-if="t.location">{{ t.location }} &middot; </span>
                                {{ formatDate(t.training_date) }}<span v-if="t.date_to"> – {{ formatDate(t.date_to) }}</span>
                            </div>
                            <div class="text-muted" style="font-size:12px;" v-if="t.conducted_by">Conducted by: {{ t.conducted_by }}</div>
                            <div class="text-muted" style="font-size:11px;" v-if="t.added_by">Added by: {{ t.added_by.name }}</div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeTraining(t)">Remove</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Equipment -->
        <div class="section pt-0 mt-2">
            <div class="wc-card">
                <div class="wc-card-header">
                    <span class="wc-card-title">Equipment</span>
                    <button type="button" class="btn btn-sm btn-primary" @click="openEquipmentModal">
                        <ion-icon name="add-outline"></ion-icon> Add
                    </button>
                </div>
                <div class="wc-card-body">
                    <div v-if="!equipment.length" class="text-center text-muted py-2">No equipment recorded.</div>
                    <div v-for="e in equipment" :key="e.id" class="wc-list-item">
                        <div class="wc-list-item-main">
                            <b>{{ e.equipment_type?.name || '-' }}</b> &middot; Qty {{ e.quantity }}
                            <span class="wc-chip" :class="'status-' + e.status">{{ statusLabel(e.status) }}</span>
                            <div class="text-muted" style="font-size:12px;">
                                <span v-if="e.distributed_at">Distributed: {{ formatDate(e.distributed_at) }}</span>
                                <span v-if="e.returned_at"> &middot; Returned: {{ formatDate(e.returned_at) }}</span>
                            </div>
                            <div class="text-muted" style="font-size:12px;" v-if="e.remarks">{{ e.remarks }}</div>
                        </div>
                        <div style="display:flex;flex-direction:column;gap:6px;">
                            <button type="button" class="btn btn-sm btn-outline-primary" @click="openEditEquipmentModal(e)">Edit</button>
                            <button type="button" class="btn btn-sm btn-outline-danger" @click="removeEquipment(e)">Remove</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Questionnaire Responses (read-only) -->
        <div class="section pt-0 mt-2" v-if="answersBySection.length">
            <div class="wc-card">
                <div class="wc-card-header">
                    <span class="wc-card-title">Questionnaire Responses</span>
                </div>
                <div class="wc-card-body">
                    <div v-for="group in answersBySection" :key="group.section" class="mb-2">
                        <div class="wc-section-title">{{ group.section }}</div>
                        <div class="wc-row wc-row-stack" v-for="a in group.answers" :key="a.id">
                            <span class="wc-label">{{ questionLabel(a.question) }}</span>
                            <span class="wc-val">{{ formatAnswer(a) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Deployment History -->
        <div class="section pt-0 mt-2 mb-3">
            <div class="wc-card">
                <div class="wc-card-header">
                    <span class="wc-card-title">Deployment History</span>
                    <button type="button" class="btn btn-sm btn-primary" @click="openDeploymentModal">
                        <ion-icon name="add-outline"></ion-icon> Add
                    </button>
                </div>
                <div class="wc-card-body">
                    <div v-if="!deployments.length" class="text-center text-muted py-2">No deployments logged.</div>
                    <div v-for="d in deployments" :key="d.id" class="wc-list-item">
                        <div class="wc-list-item-main">
                            <b>{{ d.title }}</b>
                            <div class="text-muted" style="font-size:12px;">
                                <span v-if="d.location">{{ d.location }} &middot; </span>
                                {{ formatDate(d.date_from) }}<span v-if="d.date_to"> – {{ formatDate(d.date_to) }}</span>
                            </div>
                            <div class="text-muted" style="font-size:12px;" v-if="d.description">{{ d.description }}</div>
                            <div class="text-muted" style="font-size:11px;" v-if="d.added_by_name">Added by: {{ d.added_by_name }}</div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeDeployment(d)">Remove</button>
                    </div>
                </div>
            </div>
        </div>
    </template>

    <!-- Add Training Modal -->
    <div class="modal fade action-sheet" id="wcTrainingModal" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header"><h5 class="modal-title">Add Organizational Training</h5></div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div class="form-group">
                            <label class="label">Title</label>
                            <input type="text" class="form-control" v-model="trainingForm.title" placeholder="Title">
                        </div>
                        <div class="form-group">
                            <label class="label">Location</label>
                            <input type="text" class="form-control" v-model="trainingForm.location" placeholder="Location">
                        </div>
                        <div class="form-group">
                            <label class="label">Date From</label>
                            <input type="date" class="form-control" v-model="trainingForm.training_date">
                        </div>
                        <div class="form-group">
                            <label class="label">Date To</label>
                            <input type="date" class="form-control" v-model="trainingForm.date_to">
                        </div>
                        <div class="form-group">
                            <label class="label">Conducted By</label>
                            <input type="text" class="form-control" v-model="trainingForm.conducted_by" placeholder="Conducted By">
                        </div>
                        <p class="error-feedback" v-if="trainingFormError">{{ trainingFormError }}</p>
                        <button type="button" class="btn btn-primary btn-block btn-lg mt-2" :disabled="trainingFormSaving" @click="submitTraining">
                            <span v-if="trainingFormSaving" class="spinner-border spinner-border-sm me-1"></span>
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add Equipment Modal -->
    <div class="modal fade action-sheet" id="wcEquipmentModal" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header"><h5 class="modal-title">{{ editingEquipmentId ? 'Edit Equipment' : 'Add Equipment' }}</h5></div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <template v-if="!showNewEquipmentTypeForm">
                            <div class="form-group">
                                <label class="label">Equipment Type</label>
                                <select class="form-control custom-select" v-model="equipmentForm.equipment_type_id">
                                    <option value="">Select Type</option>
                                    <option v-for="t in equipmentTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                                </select>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-primary mb-2" @click="showNewEquipmentTypeForm = true">
                                <ion-icon name="add-outline"></ion-icon> New Equipment Type
                            </button>
                            <div class="form-group">
                                <label class="label">Qty</label>
                                <input type="number" class="form-control" v-model="equipmentForm.quantity" min="1">
                            </div>
                            <div class="form-group">
                                <label class="label">Status</label>
                                <select class="form-control custom-select" v-model="equipmentForm.status">
                                    <option value="issued">Issued</option>
                                    <option value="returned">Returned</option>
                                    <option value="damaged">Damaged</option>
                                    <option value="lost">Lost</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="label">Distributed On</label>
                                <input type="date" class="form-control" v-model="equipmentForm.distributed_at">
                            </div>
                            <div class="form-group">
                                <label class="label">Returned On</label>
                                <input type="date" class="form-control" v-model="equipmentForm.returned_at">
                            </div>
                            <div class="form-group">
                                <label class="label">Remarks</label>
                                <input type="text" class="form-control" v-model="equipmentForm.remarks" placeholder="Remarks">
                            </div>
                            <p class="error-feedback" v-if="equipmentFormError">{{ equipmentFormError }}</p>
                            <button type="button" class="btn btn-primary btn-block btn-lg mt-2" :disabled="equipmentFormSaving" @click="submitEquipment">
                                <span v-if="equipmentFormSaving" class="spinner-border spinner-border-sm me-1"></span>
                                Save
                            </button>
                        </template>
                        <template v-else>
                            <p class="text-muted" style="font-size:12px;">Define a new reusable equipment type — it'll be available for every volunteer going forward.</p>
                            <div class="form-group">
                                <label class="label">Name (English)</label>
                                <input type="text" class="form-control" v-model="newEquipmentTypeForm.name" placeholder="e.g. Helmet">
                            </div>
                            <div class="form-group">
                                <label class="label">Name (Malayalam) <em>(optional)</em></label>
                                <input type="text" class="form-control mal-text" v-model="newEquipmentTypeForm.name_mal" placeholder="Malayalam name">
                            </div>
                            <div class="form-group">
                                <label class="label">Description <em>(optional)</em></label>
                                <input type="text" class="form-control" v-model="newEquipmentTypeForm.description" placeholder="Description">
                            </div>
                            <p class="error-feedback" v-if="newEquipmentTypeFormError">{{ newEquipmentTypeFormError }}</p>
                            <div style="display:flex;gap:8px;">
                                <button type="button" class="btn btn-outline-secondary flex-fill" @click="showNewEquipmentTypeForm = false">Cancel</button>
                                <button type="button" class="btn btn-primary flex-fill" :disabled="newEquipmentTypeFormSaving" @click="submitNewEquipmentType">
                                    <span v-if="newEquipmentTypeFormSaving" class="spinner-border spinner-border-sm me-1"></span>
                                    Create
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add Deployment Modal -->
    <div class="modal fade action-sheet" id="wcDeploymentModal" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header"><h5 class="modal-title">Add Deployment</h5></div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div class="form-group">
                            <label class="label">Title</label>
                            <input type="text" class="form-control" v-model="deploymentForm.title" placeholder="Title">
                        </div>
                        <div class="form-group">
                            <label class="label">Location</label>
                            <input type="text" class="form-control" v-model="deploymentForm.location" placeholder="Location">
                        </div>
                        <div class="form-group">
                            <label class="label">Date From</label>
                            <input type="date" class="form-control" v-model="deploymentForm.date_from">
                        </div>
                        <div class="form-group">
                            <label class="label">Date To</label>
                            <input type="date" class="form-control" v-model="deploymentForm.date_to">
                        </div>
                        <div class="form-group">
                            <label class="label">Description</label>
                            <textarea class="form-control" rows="2" v-model="deploymentForm.description" placeholder="Description"></textarea>
                        </div>
                        <p class="error-feedback" v-if="deploymentFormError">{{ deploymentFormError }}</p>
                        <button type="button" class="btn btn-primary btn-block btn-lg mt-2" :disabled="deploymentFormSaving" @click="submitDeployment">
                            <span v-if="deploymentFormSaving" class="spinner-border spinner-border-sm me-1"></span>
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Award Badge Modal -->
    <div class="modal fade action-sheet" id="wcBadgeModal" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header"><h5 class="modal-title">Award Verified Skill Badge</h5></div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <template v-if="!showNewBadgeForm">
                            <div class="form-group">
                                <label class="label">Badge</label>
                                <select class="form-control custom-select" v-model="badgeForm.badge_id">
                                    <option value="">Select Badge</option>
                                    <option v-for="b in badgeCatalog" :key="b.id" :value="b.id">{{ b.icon ? b.icon + ' ' : '' }}{{ b.name }}</option>
                                </select>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-primary mb-2" @click="showNewBadgeForm = true">
                                <ion-icon name="add-outline"></ion-icon> New Badge Type
                            </button>
                            <div class="form-group">
                                <label class="label">Note <em>(optional)</em></label>
                                <input type="text" class="form-control" v-model="badgeForm.note" placeholder="e.g. Confirmed via first aid camp on 12-Aug">
                            </div>
                            <p class="error-feedback" v-if="badgeFormError">{{ badgeFormError }}</p>
                            <button type="button" class="btn btn-primary btn-block btn-lg mt-2" :disabled="badgeFormSaving" @click="submitAwardBadge">
                                <span v-if="badgeFormSaving" class="spinner-border spinner-border-sm me-1"></span>
                                Award Badge
                            </button>
                        </template>
                        <template v-else>
                            <p class="text-muted" style="font-size:12px;">Define a new reusable badge type — it'll be available for every volunteer going forward.</p>
                            <div class="form-group">
                                <label class="label">Name (English)</label>
                                <input type="text" class="form-control" v-model="newBadgeForm.name" placeholder="e.g. First Aid Certified">
                            </div>
                            <div class="form-group">
                                <label class="label">Name (Malayalam) <em>(optional)</em></label>
                                <input type="text" class="form-control mal-text" v-model="newBadgeForm.name_mal" placeholder="Malayalam name">
                            </div>
                            <div class="form-group">
                                <label class="label">Description <em>(optional)</em></label>
                                <input type="text" class="form-control" v-model="newBadgeForm.description" placeholder="Description">
                            </div>
                            <div class="form-group">
                                <label class="label">Icon <em>(emoji, optional)</em></label>
                                <input type="text" class="form-control" v-model="newBadgeForm.icon" placeholder="🩹" maxlength="4">
                            </div>
                            <p class="error-feedback" v-if="newBadgeFormError">{{ newBadgeFormError }}</p>
                            <div style="display:flex;gap:8px;">
                                <button type="button" class="btn btn-outline-secondary flex-fill" @click="showNewBadgeForm = false">Cancel</button>
                                <button type="button" class="btn btn-primary flex-fill" :disabled="newBadgeFormSaving" @click="submitNewBadgeType">
                                    <span v-if="newBadgeFormSaving" class="spinner-border spinner-border-sm me-1"></span>
                                    Create
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
</template>

<script>
import UserService from '../../services/user.service';
import AlertModel from '../layouts/AlertModel.vue';
import $ from 'jquery';

export default {
    name: 'WisdomCareVolunteerDetail',
    components: { AlertModel },
    data() {
        return {
            loading: true,
            volunteer: null,
            orgTrainings: [],
            equipment: [],
            deployments: [],
            badges: [],
            badgeCatalog: [],
            equipmentTypes: [],
            verifySaving: false,
            alert_type: '',
            alert_message: '',
            trainingForm: { title: '', location: '', training_date: '', date_to: '', conducted_by: '' },
            trainingFormSaving: false,
            trainingFormError: '',
            equipmentForm: { equipment_type_id: '', quantity: 1, status: 'issued', distributed_at: '', returned_at: '', remarks: '' },
            equipmentFormSaving: false,
            equipmentFormError: '',
            editingEquipmentId: null,
            showNewEquipmentTypeForm: false,
            newEquipmentTypeForm: { name: '', name_mal: '', description: '' },
            newEquipmentTypeFormSaving: false,
            newEquipmentTypeFormError: '',
            deploymentForm: { title: '', location: '', date_from: '', date_to: '', description: '' },
            deploymentFormSaving: false,
            deploymentFormError: '',
            badgeForm: { badge_id: '', note: '' },
            badgeFormSaving: false,
            badgeFormError: '',
            showNewBadgeForm: false,
            newBadgeForm: { name: '', name_mal: '', description: '', icon: '' },
            newBadgeFormSaving: false,
            newBadgeFormError: '',
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        answersBySection() {
            if (!this.volunteer || !this.volunteer.answers) return [];
            const groups = {};
            this.volunteer.answers.forEach((a) => {
                if (!a.question) return;
                const section = a.question.section || 'Other';
                if (!groups[section]) groups[section] = [];
                groups[section].push(a);
            });
            return Object.keys(groups).map((section) => ({ section, answers: groups[section] }));
        },
    },
    mounted() {
        if (!this.currentUser) {
            location.replace('/');
            return;
        }
        this.$root.updateParent('Wisdom Care Volunteer');
        this.loadProfile();
        this.loadEquipmentTypes();
        this.loadBadgeCatalog();
    },
    methods: {
        showAlert(type, message) {
            this.alert_type = type;
            this.alert_message = message;
            this.$refs.alert_model.openModel();
        },
        formatDate(v) {
            if (!v) return '-';
            const d = new Date(v);
            if (isNaN(d.getTime())) return v;
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        statusLabel(s) {
            const map = { issued: 'Issued', returned: 'Returned', damaged: 'Damaged', lost: 'Lost' };
            return map[s] || 'Issued';
        },
        // Medal gradient for a badge — same convention as WisdomCare.vue's badgeMedalStyle(),
        // kept in sync so the medal looks identical on both the admin and self-service views.
        lightenColor(hex, percent) {
            try {
                const num = parseInt(hex.replace('#', ''), 16);
                const amt = Math.round(2.55 * percent);
                const r = Math.min(255, (num >> 16) + amt);
                const g = Math.min(255, ((num >> 8) & 0x00ff) + amt);
                const b = Math.min(255, (num & 0x0000ff) + amt);
                return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
            } catch (e) {
                return hex;
            }
        },
        badgeMedalStyle(badge) {
            if (!badge || !badge.color) return {};
            return { background: `linear-gradient(135deg, ${this.lightenColor(badge.color, 35)}, ${badge.color})` };
        },
        // Same convention as WisdomCare.vue's questionLabel() — show only the app's current
        // language instead of both, falling back to the other when one is missing.
        questionLabel(q) {
            if (!q) return '';
            if (this.$i18n.locale === 'en') {
                return q.question_text_en || q.question_text_mal;
            }
            return q.question_text_mal || q.question_text_en;
        },
        formatAnswer(a) {
            if (a.answer_options && a.answer_options.length) {
                const text = a.answer_options.map((o) => (o === '__other__' ? 'Other' : o)).join(', ');
                return a.other_text ? `${text} (${a.other_text})` : text;
            }
            const text = a.answer_text === '__other__' ? 'Other' : a.answer_text;
            return a.other_text ? `${text} (${a.other_text})` : text;
        },
        loadProfile() {
            this.loading = true;
            UserService.authPostRequest('wisdom-care/get-volunteer-profile', {
                id: parseInt(this.$route.params.volunteerId),
            }).then((response) => {
                this.loading = false;
                if (response.data.status === 'success') {
                    this.volunteer = response.data.data;
                    this.orgTrainings = this.volunteer.org_trainings || [];
                    this.equipment = this.volunteer.equipment || [];
                    this.deployments = this.volunteer.deployments || [];
                    this.badges = this.volunteer.badges || [];
                } else {
                    this.showAlert('Warning', response.data.message);
                }
            }).catch((error) => {
                this.loading = false;
                this.showAlert('Error', (error.response && error.response.data && error.response.data.message) || 'ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല');
            });
        },
        loadEquipmentTypes() {
            UserService.authPostRequest('wisdom-care/equipment-types', {}).then((response) => {
                if (response.data.status === 'success') {
                    this.equipmentTypes = response.data.data || [];
                }
            }).catch(() => {});
        },
        toggleVerify() {
            const endpoint = this.volunteer.verified ? 'wisdom-care/unverify-volunteer' : 'wisdom-care/verify-volunteer';
            this.verifySaving = true;
            UserService.authPostRequest(endpoint, { id: this.volunteer.id }).then(
                (response) => {
                    this.verifySaving = false;
                    if (response.data.status === 'success') {
                        this.volunteer = { ...this.volunteer, ...response.data.data };
                    } else {
                        this.showAlert('Warning', response.data.message);
                    }
                },
                (error) => {
                    this.verifySaving = false;
                    this.showAlert('Error', (error.response && error.response.data && error.response.data.message) || 'പിശക്.');
                }
            );
        },
        // ---- Organizational Trainings ----
        openTrainingModal() {
            this.trainingForm = { title: '', location: '', training_date: '', date_to: '', conducted_by: '' };
            this.trainingFormError = '';
            $('#wcTrainingModal').modal('show');
        },
        submitTraining() {
            if (!this.trainingForm.title.trim()) {
                this.trainingFormError = 'Title നൽകുക.';
                return;
            }
            this.trainingFormSaving = true;
            this.trainingFormError = '';
            UserService.authPostRequest('wisdom-care/volunteer-org-trainings/store', {
                volunteer_id: this.volunteer.id,
                title: this.trainingForm.title.trim(),
                location: this.trainingForm.location,
                training_date: this.trainingForm.training_date || null,
                date_to: this.trainingForm.date_to || null,
                conducted_by: this.trainingForm.conducted_by,
            }).then(
                (response) => {
                    this.trainingFormSaving = false;
                    if (response.data.status === 'success') {
                        $('#wcTrainingModal').modal('hide');
                        this.orgTrainings.unshift(response.data.data);
                    } else {
                        this.trainingFormError = response.data.message;
                    }
                },
                (error) => {
                    this.trainingFormSaving = false;
                    this.trainingFormError = (error.response && error.response.data && error.response.data.message) || 'സേവ് ചെയ്യാൻ കഴിഞ്ഞില്ല.';
                }
            );
        },
        removeTraining(t) {
            if (!window.confirm('Remove this training record?')) return;
            UserService.authPostRequest('wisdom-care/volunteer-org-trainings/delete', { id: t.id }).then(() => {
                this.orgTrainings = this.orgTrainings.filter((x) => x.id !== t.id);
            }).catch((error) => {
                this.showAlert('Error', (error.response && error.response.data && error.response.data.message) || 'പിശക്.');
            });
        },
        // ---- Equipment ----
        openEquipmentModal() {
            this.editingEquipmentId = null;
            this.equipmentForm = { equipment_type_id: '', quantity: 1, status: 'issued', distributed_at: '', returned_at: '', remarks: '' };
            this.equipmentFormError = '';
            this.showNewEquipmentTypeForm = false;
            this.newEquipmentTypeForm = { name: '', name_mal: '', description: '' };
            this.newEquipmentTypeFormError = '';
            $('#wcEquipmentModal').modal('show');
        },
        // Reuses the same Add Equipment modal/form in "edit mode" — needed to actually update an
        // item's status over its lifecycle (issued → returned/damaged/lost) instead of the only
        // prior option being delete-and-re-add, which threw away the original distributed_at.
        openEditEquipmentModal(e) {
            this.editingEquipmentId = e.id;
            this.equipmentForm = {
                equipment_type_id: e.equipment_type_id,
                quantity: e.quantity || 1,
                status: e.status || 'issued',
                distributed_at: e.distributed_at ? e.distributed_at.slice(0, 10) : '',
                returned_at: e.returned_at ? e.returned_at.slice(0, 10) : '',
                remarks: e.remarks || '',
            };
            this.equipmentFormError = '';
            this.showNewEquipmentTypeForm = false;
            $('#wcEquipmentModal').modal('show');
        },
        submitNewEquipmentType() {
            if (!this.newEquipmentTypeForm.name.trim()) {
                this.newEquipmentTypeFormError = 'Name നൽകുക.';
                return;
            }
            this.newEquipmentTypeFormSaving = true;
            this.newEquipmentTypeFormError = '';
            UserService.authPostRequest('wisdom-care/store-equipment-type', {
                name: this.newEquipmentTypeForm.name.trim(),
                name_mal: this.newEquipmentTypeForm.name_mal,
                description: this.newEquipmentTypeForm.description,
            }).then(
                (response) => {
                    this.newEquipmentTypeFormSaving = false;
                    if (response.data.status === 'success') {
                        this.equipmentTypes.push(response.data.data);
                        this.equipmentForm.equipment_type_id = response.data.data.id;
                        this.showNewEquipmentTypeForm = false;
                    } else {
                        this.newEquipmentTypeFormError = response.data.message;
                    }
                },
                (error) => {
                    this.newEquipmentTypeFormSaving = false;
                    this.newEquipmentTypeFormError = (error.response && error.response.data && error.response.data.message) || 'സേവ് ചെയ്യാൻ കഴിഞ്ഞില്ല.';
                }
            );
        },
        submitEquipment() {
            if (!this.equipmentForm.equipment_type_id) {
                this.equipmentFormError = 'Equipment Type തിരഞ്ഞെടുക്കുക.';
                return;
            }
            this.equipmentFormSaving = true;
            this.equipmentFormError = '';
            const payload = {
                equipment_type_id: parseInt(this.equipmentForm.equipment_type_id),
                quantity: parseInt(this.equipmentForm.quantity) || 1,
                status: this.equipmentForm.status,
                distributed_at: this.equipmentForm.distributed_at || null,
                returned_at: this.equipmentForm.returned_at || null,
                remarks: this.equipmentForm.remarks,
            };
            const endpoint = this.editingEquipmentId ? 'wisdom-care/volunteer-equipment/update' : 'wisdom-care/volunteer-equipment/store';
            if (this.editingEquipmentId) {
                payload.id = this.editingEquipmentId;
            } else {
                payload.volunteer_id = this.volunteer.id;
            }
            UserService.authPostRequest(endpoint, payload).then(
                (response) => {
                    this.equipmentFormSaving = false;
                    if (response.data.status === 'success') {
                        $('#wcEquipmentModal').modal('hide');
                        this.loadVolunteerEquipment();
                    } else {
                        this.equipmentFormError = response.data.message;
                    }
                },
                (error) => {
                    this.equipmentFormSaving = false;
                    this.equipmentFormError = (error.response && error.response.data && error.response.data.message) || 'സേവ് ചെയ്യാൻ കഴിഞ്ഞില്ല.';
                }
            );
        },
        loadVolunteerEquipment() {
            UserService.authPostRequest('wisdom-care/volunteer-equipment/list', { volunteer_id: this.volunteer.id }).then((response) => {
                if (response.data.status === 'success') {
                    this.equipment = response.data.data || [];
                }
            }).catch(() => {});
        },
        removeEquipment(e) {
            if (!window.confirm('Remove this equipment?')) return;
            UserService.authPostRequest('wisdom-care/volunteer-equipment/delete', { id: e.id }).then(() => {
                this.equipment = this.equipment.filter((x) => x.id !== e.id);
            }).catch((error) => {
                this.showAlert('Error', (error.response && error.response.data && error.response.data.message) || 'പിശക്.');
            });
        },
        // ---- Deployments ----
        openDeploymentModal() {
            this.deploymentForm = { title: '', location: '', date_from: '', date_to: '', description: '' };
            this.deploymentFormError = '';
            $('#wcDeploymentModal').modal('show');
        },
        submitDeployment() {
            if (!this.deploymentForm.title.trim() || !this.deploymentForm.date_from) {
                this.deploymentFormError = 'Title, Date From നൽകുക.';
                return;
            }
            this.deploymentFormSaving = true;
            this.deploymentFormError = '';
            UserService.authPostRequest('wisdom-care/volunteer-deployments/store', {
                volunteer_id: this.volunteer.id,
                title: this.deploymentForm.title.trim(),
                location: this.deploymentForm.location,
                date_from: this.deploymentForm.date_from,
                date_to: this.deploymentForm.date_to || null,
                description: this.deploymentForm.description,
            }).then(
                (response) => {
                    this.deploymentFormSaving = false;
                    if (response.data.status === 'success') {
                        $('#wcDeploymentModal').modal('hide');
                        this.deployments.unshift(response.data.data);
                    } else {
                        this.deploymentFormError = response.data.message;
                    }
                },
                (error) => {
                    this.deploymentFormSaving = false;
                    this.deploymentFormError = (error.response && error.response.data && error.response.data.message) || 'സേവ് ചെയ്യാൻ കഴിഞ്ഞില്ല.';
                }
            );
        },
        removeDeployment(d) {
            if (!window.confirm('Remove this deployment record?')) return;
            UserService.authPostRequest('wisdom-care/volunteer-deployments/delete', { id: d.id }).then(() => {
                this.deployments = this.deployments.filter((x) => x.id !== d.id);
            }).catch((error) => {
                this.showAlert('Error', (error.response && error.response.data && error.response.data.message) || 'പിശക്.');
            });
        },
        // ---- Verified Skill Badges ----
        loadBadgeCatalog() {
            UserService.authPostRequest('wisdom-care/get-badges', {}).then((response) => {
                if (response.data.status === 'success') {
                    this.badgeCatalog = response.data.data || [];
                }
            }).catch(() => {});
        },
        openBadgeModal() {
            this.badgeForm = { badge_id: '', note: '' };
            this.badgeFormError = '';
            this.showNewBadgeForm = false;
            this.newBadgeForm = { name: '', name_mal: '', description: '', icon: '' };
            this.newBadgeFormError = '';
            $('#wcBadgeModal').modal('show');
        },
        submitAwardBadge() {
            if (!this.badgeForm.badge_id) {
                this.badgeFormError = 'Badge തിരഞ്ഞെടുക്കുക.';
                return;
            }
            this.badgeFormSaving = true;
            this.badgeFormError = '';
            UserService.authPostRequest('wisdom-care/volunteer-badges/award', {
                volunteer_id: this.volunteer.id,
                badge_id: parseInt(this.badgeForm.badge_id),
                note: this.badgeForm.note,
            }).then(
                (response) => {
                    this.badgeFormSaving = false;
                    if (response.data.status === 'success') {
                        $('#wcBadgeModal').modal('hide');
                        this.badges.unshift(response.data.data);
                    } else {
                        this.badgeFormError = response.data.message;
                    }
                },
                (error) => {
                    this.badgeFormSaving = false;
                    this.badgeFormError = (error.response && error.response.data && error.response.data.message) || 'നൽകാൻ കഴിഞ്ഞില്ല.';
                }
            );
        },
        submitNewBadgeType() {
            if (!this.newBadgeForm.name.trim()) {
                this.newBadgeFormError = 'Name നൽകുക.';
                return;
            }
            this.newBadgeFormSaving = true;
            this.newBadgeFormError = '';
            UserService.authPostRequest('wisdom-care/store-badge', {
                name: this.newBadgeForm.name.trim(),
                name_mal: this.newBadgeForm.name_mal,
                description: this.newBadgeForm.description,
                icon: this.newBadgeForm.icon,
            }).then(
                (response) => {
                    this.newBadgeFormSaving = false;
                    if (response.data.status === 'success') {
                        this.badgeCatalog.push(response.data.data);
                        this.badgeForm.badge_id = response.data.data.id;
                        this.showNewBadgeForm = false;
                    } else {
                        this.newBadgeFormError = response.data.message;
                    }
                },
                (error) => {
                    this.newBadgeFormSaving = false;
                    this.newBadgeFormError = (error.response && error.response.data && error.response.data.message) || 'സേവ് ചെയ്യാൻ കഴിഞ്ഞില്ല.';
                }
            );
        },
        revokeBadge(vb) {
            if (!window.confirm(`Revoke the "${vb.badge?.name}" badge from this volunteer?`)) return;
            UserService.authPostRequest('wisdom-care/volunteer-badges/revoke', { id: vb.id }).then(() => {
                this.badges = this.badges.filter((x) => x.id !== vb.id);
            }).catch((error) => {
                this.showAlert('Error', (error.response && error.response.data && error.response.data.message) || 'പിശക്.');
            });
        },
    },
};
</script>

<style scoped>
.wc-card {
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 14px;
    padding: 4px 16px 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, .04);
}
.wc-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px solid #f3f4f8;
    gap: 8px;
}
.wc-card-title {
    font-size: 14px;
    font-weight: 800;
    color: #27173E;
}
.wc-card-body {
    padding: 10px 0 0;
}
.wc-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 10px;
    padding: 7px 0;
    border-top: 1px solid #f3f4f8;
    font-size: 13px;
}
.wc-row:first-child {
    border-top: none;
}
.wc-label {
    color: #6c757d;
    flex-shrink: 0;
}
.wc-val {
    color: #27173E;
    font-weight: 600;
    text-align: left;
    word-break: break-word;
    overflow-wrap: anywhere;
    min-width: 0;
    flex: 1 1 auto;
}
/* Questionnaire answers pair a long label with a long free-text answer — side-by-side never
   has room for both, so stack label above answer instead of splitting the row's width. */
.wc-row-stack {
    flex-direction: column;
    align-items: stretch;
}
.wc-row-stack .wc-val {
    text-align: left;
}
.wc-service-categories {
    padding-top: 10px;
}
.wc-chip {
    display: inline-block;
    margin: 2px 4px 2px 0;
    padding: 2px 10px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
    background: #e7ecff;
    color: #3b5bdb;
}
.wc-chip-custom {
    background: #f3e8ff;
    color: #7c3aed;
}
/* Small shiny medal icon, same look as WisdomCare.vue's larger .wc-badge-medal, scaled down
   to sit inline in this list row alongside the note/awarded-by/revoke details. */
.wc-badge-medal-sm {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #ffe08a, #f0a416);
    border: 2px solid #fff8e6;
    box-shadow:
        0 3px 7px rgba(197, 132, 4, 0.4),
        inset 0 -3px 5px rgba(140, 87, 0, 0.35),
        inset 0 2px 4px rgba(255, 255, 255, 0.65);
}
.wc-badge-medal-sm ion-icon {
    font-size: 17px;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.25));
}
.wc-chip.status-issued { background: #e7ecff; color: #3b5bdb; }
.wc-chip.status-returned { background: #e6f7ec; color: #15803d; }
.wc-chip.status-damaged,
.wc-chip.status-lost { background: #fdeaea; color: #c0392b; }
.wc-verified-badge {
    padding: 2px 10px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
}
.wc-verified-badge.verified {
    background: #e6f7ec;
    color: #15803d;
}
.wc-verified-badge.not-verified {
    background: #f3f4f6;
    color: #6b7280;
}
.wc-section-title {
    font-size: 12px;
    font-weight: 800;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin: 6px 0 4px;
}
.wc-list-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 0;
    border-top: 1px solid #f3f4f8;
}
.wc-list-item:first-child {
    border-top: none;
}
.wc-list-item-main {
    flex: 1;
    min-width: 0;
    font-size: 13px;
}
</style>
